import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Gauge, Link2, Loader2, RefreshCw, TrendingUp } from 'lucide-react';
import { Banner, BrandIcon, Button, Fact, Facts, Spinner, StatusPill } from '@/components/ui';
import { ErrorState } from '@/components/ErrorState';
import { useRoles } from '@/hooks/useAuth';
import { useRefreshSeoMetrics, useSiteSeoMetrics } from '@/hooks/useSeoMetrics';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import { can } from '@/utils/permissions';
import { DOMAIN_VERDICT, SEO_SEVERITY, domainNoteText } from '@/utils/seo';
import type { DomainMetricsView, IntegrationKind, MetricSource } from '@/types/api';

const number = (value: number | null | undefined) => (value === null || value === undefined ? '—' : value.toLocaleString('ru-RU'));
const NOTE_SEVERITY: Record<string, string> = { risk: 'critical', care: 'warning', info: 'info' };

/**
 * The sources' facts, each with where it stands. `reading` marks the sources
 * being read this moment, so the card shows the read going on rather than
 * old figures. Shared with the domain check on the Domains page.
 */
export function MetricsFacts({
  view,
  sources,
  reading = [],
}: {
  readonly view: DomainMetricsView;
  readonly sources?: readonly string[];
  readonly reading?: readonly MetricSource[];
}): ReactNode {
  const roles = useRoles();
  const missing = (kind: IntegrationKind) => (
    <span className="faint">
      не подключён{can(roles, 'secrets.manage') ? <> · <Link to={`/integrations/${kind}`}>Интеграции</Link></> : null}
    </span>
  );
  const pending = <span className="faint">ещё не прочитан</span>;
  const inProgress = (
    <span className="row-tight muted">
      <Loader2 size={14} className="spin" aria-hidden="true" />
      читается…
    </span>
  );
  const failed = (error: string | null) => <span className="tone-down">{error}</span>;
  const off = (source: MetricSource) => sources !== undefined && !sources.includes(source);
  const wayback = view.wayback?.data;
  const opr = view.openpagerank?.data;
  const dfs = view.dataforseo?.data;
  const ahrefs = view.ahrefs?.data;

  return (
    <Facts>
      <Fact label="Web Archive" icon={<BrandIcon name="internetarchive" size={14} />}>
        {reading.includes('wayback') ? (
          inProgress
        ) : wayback ? (
          wayback.years.length === 0 ? (
            'снимков нет'
          ) : (
            <span>
              с {wayback.years[0]} года · лет в архиве: {wayback.years.length}
              {wayback.lastCapture ? <span className="muted"> · последний снимок {wayback.lastCapture}</span> : null}
            </span>
          )
        ) : view.wayback?.error ? (
          failed(view.wayback.error)
        ) : (
          pending
        )}
      </Fact>
      <Fact label="Ahrefs" icon={<TrendingUp size={14} aria-hidden="true" />}>
        {reading.includes('ahrefs') ? (
          inProgress
        ) : ahrefs ? (
          <span>
            DR {number(ahrefs.domainRating)}
            {ahrefs.referringDomains !== null ? <span className="muted"> · доменов ссылается {number(ahrefs.referringDomains)}</span> : null}
            {ahrefs.organicTraffic !== null ? <span className="muted"> · трафик {number(ahrefs.organicTraffic)} в мес.</span> : null}
            {ahrefs.historyError ? <span className="muted"> · история: {ahrefs.historyError}</span> : null}
          </span>
        ) : view.ahrefs?.error ? (
          failed(view.ahrefs.error)
        ) : off('ahrefs') ? (
          missing('ahrefs')
        ) : (
          pending
        )}
      </Fact>
      <Fact label="Open PageRank" icon={<Gauge size={14} aria-hidden="true" />}>
        {reading.includes('openpagerank') ? (
          inProgress
        ) : opr ? (
          opr.found ? (
            <span>
              {opr.pageRank?.toLocaleString('ru-RU', { maximumFractionDigits: 2 }) ?? '—'} из 10
              <span className="muted"> · место {number(opr.rank)} · доменов ссылается {number(opr.referringDomains)}</span>
            </span>
          ) : (
            'нет в графе ссылок'
          )
        ) : view.openpagerank?.error ? (
          failed(view.openpagerank.error)
        ) : off('openpagerank') ? (
          missing('openpagerank')
        ) : (
          pending
        )}
      </Fact>
      <Fact label="DataForSEO" icon={<Link2 size={14} aria-hidden="true" />}>
        {reading.includes('dataforseo') ? (
          inProgress
        ) : dfs ? (
          <span>
            ранг {number(dfs.rank)} · ссылок {number(dfs.backlinks)} · доменов {number(dfs.referringDomains)}
            <span className="muted"> · спам {number(dfs.spamScore ?? dfs.backlinksSpamScore)}/100</span>
          </span>
        ) : view.dataforseo?.error ? (
          failed(view.dataforseo.error)
        ) : off('dataforseo') ? (
          missing('dataforseo')
        ) : (
          pending
        )}
      </Fact>
    </Facts>
  );
}

export function MetricsNotes({ view }: { readonly view: DomainMetricsView }): ReactNode {
  if (view.notes.length === 0) return null;
  return (
    <ul className="list-plain seo-issues">
      {view.notes.map((note) => (
        <li key={`${note.code}-${JSON.stringify(note.params)}`} className="row-tight">
          <StatusPill map={SEO_SEVERITY} status={NOTE_SEVERITY[note.severity] ?? 'info'} />
          <span>{domainNoteText(note)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * A site's domain as outside sources see it (ADR-0050): history, authority,
 * links. «Обновить» reads every source there and then, each row showing its
 * read under way; one asked for in bulk waits for the worker, and says so.
 */
export function SeoMetricsCard({ siteId }: { readonly siteId: string }): ReactNode {
  const roles = useRoles();
  const metrics = useSiteSeoMetrics(siteId);
  const refresh = useRefreshSeoMetrics(siteId, {
    onSuccess: () => toast.success('Показатели обновлены'),
    onError: (error) => toast.error('Не удалось обновить', errorText(error)),
  });
  const view = metrics.data;
  const fetched = [view?.wayback?.fetchedAt, view?.ahrefs?.fetchedAt, view?.openpagerank?.fetchedAt, view?.dataforseo?.fetchedAt]
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);
  const reading: readonly MetricSource[] = refresh.isPending ? (view?.sources ?? ['wayback']) : [];

  return (
    <section className="card">
      <header className="card-head">
        <h3 className="card-title">
          <Gauge size={16} aria-hidden="true" />
          Показатели домена
        </h3>
        <div className="row-tight">
          {view ? <StatusPill map={DOMAIN_VERDICT} status={view.verdict ?? 'none'} /> : null}
          {can(roles, 'sites.deploy') ? (
            <Button size="sm" icon={RefreshCw} busy={refresh.isPending} onClick={() => refresh.mutate()}>
              {refresh.isPending ? 'Читаем' : 'Обновить'}
            </Button>
          ) : null}
        </div>
      </header>
      <div className="card-body stack">
        {metrics.isPending ? (
          <Spinner />
        ) : metrics.isError ? (
          <ErrorState error={metrics.error} />
        ) : view ? (
          <>
            {view.pending && !refresh.isPending ? (
              <Banner icon={RefreshCw} spin>
                В очереди на обновление: панель читает по два домена в минуту.
              </Banner>
            ) : null}
            <MetricsFacts view={view} sources={view.sources} reading={reading} />
            <MetricsNotes view={view} />
            {fetched ? <p className="muted">Прочитано {formatRelative(fetched)} · {view.domain}</p> : null}
          </>
        ) : null}
      </div>
    </section>
  );
}
